// src/pages/ClienteAssocia.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const API_BASE = '/api';

// helper
const norm = (v) => (v ?? '').toString().trim().toLowerCase();

export default function ClienteAssocia() {
  const navigate = useNavigate();

  // clienti
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  // lavorazioni non collegate
  const [works, setWorks] = useState([]);
  const [loadingWorks, setLoadingWorks] = useState(false);

  // lavorazioni già collegate al cliente selezionato
  const [assignedWorks, setAssignedWorks] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [selectedAssignedIds, setSelectedAssignedIds] = useState([]); // array di _id (workOrder)

  // selezione righe (batch assign)
  const [selectedIds, setSelectedIds] = useState([]); // array di _id

  // UI feedback
  const [assigning, setAssigning] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // filtri (stile Lavorazioni: search potente + dropdown rapidi)
  const [filters, setFilters] = useState({
    q: '',
    type: 'all',
    status: 'all',
  });

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const res = await authFetch(`${API_BASE}/users`);
      if (!res.ok) return;
      const data = await res.json();
      setClients((data || []).filter((u) => u.role === 'cliente'));
    } catch (e) {
      console.error('Errore loadClients:', e);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadUnassignedWorks = async () => {
    setLoadingWorks(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/work/unassigned`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || 'Errore nel recupero lavorazioni non collegate.');
        setWorks([]);
        return;
      }
      const data = await res.json();
      setWorks(Array.isArray(data) ? data : []);
      setSelectedIds([]); // reset selezione quando ricarico
    } catch (e) {
      console.error('Errore loadUnassignedWorks:', e);
      setError('Errore nella richiesta.');
      setWorks([]);
    } finally {
      setLoadingWorks(false);
    }
  };

  const loadAssignedWorks = async (customerUserId) => {
    if (!customerUserId) {
      setAssignedWorks([]);
      setSelectedAssignedIds([]);
      return;
    }

    setLoadingAssigned(true);
    try {
      const res = await authFetch(`${API_BASE}/work/by-customer/${customerUserId}`);
      if (!res.ok) {
        setAssignedWorks([]);
        return;
      }
      const data = await res.json();
      setAssignedWorks(Array.isArray(data) ? data : []);
      setSelectedAssignedIds([]);
    } catch (e) {
      console.error('Errore loadAssignedWorks:', e);
      setAssignedWorks([]);
    } finally {
      setLoadingAssigned(false);
    }
  };

  useEffect(() => {
    loadClients();
    loadUnassignedWorks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const filteredWorks = useMemo(() => {
    const q = norm(filters.q);
    const type = norm(filters.type);
    const status = norm(filters.status);

    return (works || []).filter((w) => {
      if (type !== 'all' && norm(w.type) !== type) return false;
      if (status !== 'all' && norm(w.status) !== status) return false;

      if (!q) return true;

      const hay = [
        w.internalCode,
        w.externalCode,
        w.companyName,
        w.customerPhone,
        w.place,
        w.type,
        w.status,
        w.description,
        w.equipmentType,
        w.equipmentMaker,
        w.equipmentModel,
        w.equipmentSerial,
        w.equipmentInventory,
        w.equipmentPresidio,
        w.rifPreventivo,
        w.preventivoStatus,
        w.orderStatus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [works, filters]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allFilteredSelected =
    filteredWorks.length > 0 && filteredWorks.every((w) => selectedSet.has(w._id));

  const toggleSelectAllFiltered = () => {
    if (filteredWorks.length === 0) return;

    setSelectedIds((prev) => {
      const prevSet = new Set(prev);

      if (filteredWorks.every((w) => prevSet.has(w._id))) {
        for (const w of filteredWorks) prevSet.delete(w._id);
        return Array.from(prevSet);
      }

      for (const w of filteredWorks) prevSet.add(w._id);
      return Array.from(prevSet);
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const getSelectedClientLabel = () => {
    const c = clients.find((x) => x._id === selectedClientId);
    if (!c) return 'cliente selezionato';
    return `${(c.nome || '').trim() || c.username} (${c.matricola || 'C****'})`;
  };

  const handleAssign = async () => {
    setError('');
    setMsg('');

    if (!selectedClientId) {
      setError('Seleziona un cliente.');
      return;
    }
    if (selectedIds.length === 0) {
      setError('Seleziona almeno una lavorazione.');
      return;
    }

    setAssigning(true);
    const selectedCodes = works
      .filter((w) => selectedIds.includes(w._id))
      .map((w) => w.internalCode || w.externalCode || w._id)
      .slice(0, 10); // ne mostro max 10 nel banner
    try {
      const res = await authFetch(`${API_BASE}/work/assign-customer`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerUserId: selectedClientId,
          workOrderIds: selectedIds,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Errore durante associazione.');
        return;
      }

      const client = clients.find((c) => c._id === selectedClientId);
      const clientLabel = client
        ? `${(client.nome || '').trim() || client.username} (${client.matricola || 'C****'})`
        : 'cliente selezionato';
      
      setMsg(
        `✅ Caricate ${selectedIds.length} lavorazioni su ${clientLabel}. ` +
        (selectedCodes.length ? `Codici: ${selectedCodes.join(', ')}${selectedIds.length > 10 ? '…' : ''}` : '')
      );

      // ricarico liste
      await loadUnassignedWorks();
      await loadAssignedWorks(selectedClientId);
    } catch (e) {
      console.error('Errore handleAssign:', e);
      setError('Errore nella richiesta.');
    } finally {
      setAssigning(false);
    }
  };

  // ---------------------------
  // assegnate: selezione + rimozione
  // ---------------------------
  const assignedSet = useMemo(() => new Set(selectedAssignedIds), [selectedAssignedIds]);

  const toggleAssignedOne = (id) => {
    setSelectedAssignedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allAssignedSelected =
    assignedWorks.length > 0 && assignedWorks.every((w) => assignedSet.has(w._id));

  const toggleSelectAllAssigned = () => {
    if (assignedWorks.length === 0) return;

    setSelectedAssignedIds((prev) => {
      const prevSet = new Set(prev);

      if (assignedWorks.every((w) => prevSet.has(w._id))) {
        for (const w of assignedWorks) prevSet.delete(w._id);
        return Array.from(prevSet);
      }

      for (const w of assignedWorks) prevSet.add(w._id);
      return Array.from(prevSet);
    });
  };

  const handleUnassign = async () => {
    setError('');
    setMsg('');

    if (!selectedClientId) {
      setError('Seleziona un cliente.');
      return;
    }

    if (selectedAssignedIds.length === 0) {
      setError('Seleziona almeno una lavorazione da rimuovere.');
      return;
    }

    setUnassigning(true);
    try {
      const res = await authFetch(`${API_BASE}/work/unassign-customer`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workOrderIds: selectedAssignedIds }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Errore durante rimozione.');
        return;
      }

      setMsg(`✅ Rimosse ${selectedAssignedIds.length} lavorazioni da ${getSelectedClientLabel()}.`);

      await loadAssignedWorks(selectedClientId);
      await loadUnassignedWorks();
    } catch (e) {
      console.error('Errore handleUnassign:', e);
      setError('Errore nella richiesta.');
    } finally {
      setUnassigning(false);
    }
  };

  // dropdown values “robusti” (senza hardcode infinito)
  const availableTypes = useMemo(() => {
    const set = new Set((works || []).map((w) => norm(w.type)).filter(Boolean));
    return Array.from(set).sort();
  }, [works]);

  const availableStatuses = useMemo(() => {
    const set = new Set((works || []).map((w) => norm(w.status)).filter(Boolean));
    return Array.from(set).sort();
  }, [works]);

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-7xl mx-auto pt-10 px-4">
        <Card className="shadow-md p-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Associa lavorazioni a Cliente
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Collega record storici a un account cliente (customerUserId).
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-semibold"
              >
                ← Dashboard
              </button>

              <button
                type="button"
                onClick={loadUnassignedWorks}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-semibold"
                disabled={loadingWorks || assigning || unassigning}
              >
                Aggiorna lista
              </button>
            </div>
          </div>

          {(error || msg) && (
            <div className="mt-4 space-y-2">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}
              {msg && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
                  {msg}
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-3">
            {/* Cliente */}
            <div className="lg:col-span-4">
              <label className="block text-gray-700 font-medium mb-1">
                Cliente (account)
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedClientId(id);
                  // appena selezioni un cliente, carico le lavorazioni già collegate
                  loadAssignedWorks(id);
                }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                disabled={loadingClients}
              >
                <option value="">
                  {loadingClients ? 'Carico clienti...' : '— Seleziona cliente —'}
                </option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {(c.nome || '').trim() || c.username} ({c.matricola || 'C****'})
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="lg:col-span-5">
              <label className="block text-gray-700 font-medium mb-1">
                Filtra (tutti i campi)
              </label>
              <input
                type="text"
                name="q"
                value={filters.q}
                onChange={handleFilterChange}
                placeholder="Cerca per codice, azienda, luogo, descrizione, modello, seriale, ecc..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <p className="mt-1 text-[11px] text-gray-400">
                Filtro live su codice, azienda, luogo, descrizione, matricole, modelli, seriali…
              </p>
            </div>

            {/* Dropdown rapidi */}
            <div className="lg:col-span-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Tipo
                </label>
                <select
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                >
                  <option value="all">Tutti</option>
                  {availableTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Stato
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                >
                  <option value="all">Tutti</option>
                  {availableStatuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stats + actions */}
          <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-600">
              Totale non collegate: <span className="font-semibold">{works.length}</span> —&nbsp;
              Filtrate: <span className="font-semibold">{filteredWorks.length}</span> —&nbsp;
              Selezionate: <span className="font-semibold">{selectedIds.length}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={toggleSelectAllFiltered}
                disabled={filteredWorks.length === 0}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-semibold disabled:opacity-60"
              >
                {allFilteredSelected ? 'Deseleziona filtrate' : 'Seleziona filtrate'}
              </button>

              <button
                type="button"
                onClick={clearSelection}
                disabled={selectedIds.length === 0}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-semibold disabled:opacity-60"
              >
                Pulisci selezione
              </button>

              <button
                type="button"
                onClick={handleAssign}
                disabled={assigning || loadingWorks}
                className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-semibold disabled:opacity-60"
              >
                {assigning ? 'Associo...' : 'Associa selezionate'}
              </button>
            </div>
          </div>

          {/* Table unassigned */}
          <Card className="mt-6 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-700">
                Lavorazioni non collegate (scroll, max 20 visibili)
              </div>
              <div className="text-xs text-gray-500">
                Usa rotellina + shift/trackpad per scroll orizzontale.
              </div>
            </div>

            {loadingWorks ? (
              <div className="p-4 text-sm text-gray-600">Caricamento...</div>
            ) : filteredWorks.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">
                Nessuna lavorazione trovata con i filtri attuali.
              </div>
            ) : (
              <div className="overflow-auto max-h-[520px]">
                <table className="min-w-max w-full border-separate border-spacing-0 text-[11px]">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-gray-700">
                      <th className="border-b border-gray-200 px-3 py-2 text-left w-[44px] bg-gray-50">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={toggleSelectAllFiltered}
                        />
                      </th>

                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Codice interno
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Codice esterno
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Azienda / Cliente
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Luogo
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Tipo
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Stato
                      </th>

                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Modello
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Matricola
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Inventario
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Presidio
                      </th>

                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Preventivo
                      </th>

                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">
                        Descrizione
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredWorks.map((w) => {
                      const checked = selectedSet.has(w._id);

                      return (
                        <tr key={w._id} className="hover:bg-gray-50">
                          <td className="border-b border-gray-100 px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleOne(w._id)}
                            />
                          </td>

                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.internalCode || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.externalCode || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.companyName || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.place || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap capitalize">
                            {w.type || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.status || '-'}
                          </td>

                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.equipmentModel || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.equipmentSerial || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.equipmentInventory || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.equipmentPresidio || '-'}
                          </td>

                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.preventivoStatus || w.rifPreventivo || '-'}
                          </td>

                          <td className="border-b border-gray-100 px-3 py-2 align-top">
                            <span className="block max-w-[520px] truncate">
                              {w.description || '-'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Assigned list */}
          <Card className="mt-8 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-700">
                Lavorazioni già collegate a: <span className="font-bold">{getSelectedClientLabel()}</span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => loadAssignedWorks(selectedClientId)}
                  disabled={!selectedClientId || loadingAssigned || unassigning}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-semibold disabled:opacity-60"
                >
                  Aggiorna
                </button>

                <button
                  type="button"
                  onClick={handleUnassign}
                  disabled={!selectedClientId || selectedAssignedIds.length === 0 || unassigning}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold disabled:opacity-60"
                >
                  {unassigning ? 'Rimuovo...' : `Rimuovi selezionate (${selectedAssignedIds.length})`}
                </button>
              </div>
            </div>

            {!selectedClientId ? (
              <div className="p-4 text-sm text-gray-500">Seleziona un cliente per vedere le lavorazioni collegate.</div>
            ) : loadingAssigned ? (
              <div className="p-4 text-sm text-gray-600">Caricamento...</div>
            ) : assignedWorks.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">Nessuna lavorazione collegata a questo cliente.</div>
            ) : (
              <div className="overflow-auto max-h-[520px]">
                <table className="min-w-max w-full border-separate border-spacing-0 text-[11px]">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-gray-700">
                      <th className="border-b border-gray-200 px-3 py-2 text-left w-[44px] bg-gray-50">
                        <input
                          type="checkbox"
                          checked={allAssignedSelected}
                          onChange={toggleSelectAllAssigned}
                        />
                      </th>

                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">Codice interno</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">Codice esterno</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">Azienda / Cliente</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">Luogo</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">Tipo</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">Stato</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left bg-gray-50">Descrizione</th>
                    </tr>
                  </thead>

                  <tbody>
                    {assignedWorks.map((w) => {
                      const checked = assignedSet.has(w._id);
                      return (
                        <tr key={w._id} className="hover:bg-gray-50">
                          <td className="border-b border-gray-100 px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAssignedOne(w._id)}
                            />
                          </td>

                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.internalCode || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.externalCode || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.companyName || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.place || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap capitalize">
                            {w.type || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2 align-top whitespace-nowrap">
                            {w.status || '-'}
                          </td>

                          <td className="border-b border-gray-100 px-3 py-2 align-top">
                            <span className="block max-w-[520px] truncate">
                              {w.description || '-'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <p className="mt-4 text-[11px] text-gray-400">
            Nota: sopra vedi le lavorazioni non collegate (customerUserId vuoto). Sotto vedi quelle già collegate al cliente selezionato.
          </p>
        </Card>
      </div>
    </div>
  );
}
